'use client';

/**
 * LodgeSearchResults — renders the units returned by searchLodges(),
 * grouped by lodge (in `lib/data/lodges.ts` order), for /booking/lodges.
 *
 * Reuses the card/badge/pricing classes already defined in ./page.module.css
 * (the same ones LodgeUnitsClient uses for its per-lodge unit list) so the
 * search results look consistent with the rest of the booking flow without
 * duplicating styles.
 *
 * @see components/booking/LodgeSearchBar.tsx
 * @see lib/booking/search-service.ts
 */

import Link from 'next/link';
import Image from 'next/image';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { lodges } from '@/lib/data/lodges';
import { useSoldOut } from '@/hooks/useSoldOut';
import { useExchangeRate } from '@/lib/hooks/useExchangeRate';
import type { LodgeSearchResultItem } from '@/lib/booking/search-service';
import styles from './page.module.css';
// Same shimmer-block skeleton UnitBookingClient.tsx uses while a price is
// mid-conversion (never show a USD figure computed from the CLP->USD
// fallback rate). Imported, not duplicated, per project convention.
import skeletonStyles from './[slug]/LodgeBookingClient.module.css';

export interface LodgeSearchResultsProps {
  results: LodgeSearchResultItem[];
  nights: number;
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD
  guests: number;
  /**
   * True when `results` was already scoped to a single lodge (e.g. a search
   * run with `locationId` from inside /booking/lodges/{lodgeId}). Suppresses
   * the per-group lodge name header, which is redundant when the visitor is
   * already inside that lodge's page.
   */
  scopedToLodge?: boolean;
}

interface ResultsGroup {
  lodgeId: string | null;
  lodgeName: string | null;
  items: LodgeSearchResultItem[];
}

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80';

function formatUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Group results by lodge, respecting the order lodges appear in
 * lib/data/lodges.ts. Results whose locationId doesn't match any known
 * lodge are kept (never dropped) in a trailing, header-less group.
 */
function groupResultsByLodge(results: LodgeSearchResultItem[]): ResultsGroup[] {
  const byLodgeId = new Map<string, LodgeSearchResultItem[]>();
  const unmatched: LodgeSearchResultItem[] = [];

  for (const result of results) {
    const lodge = lodges.find((l) => l.id === result.locationId);
    if (!lodge) {
      unmatched.push(result);
      continue;
    }
    const bucket = byLodgeId.get(lodge.id);
    if (bucket) {
      bucket.push(result);
    } else {
      byLodgeId.set(lodge.id, [result]);
    }
  }

  const groups: ResultsGroup[] = [];
  for (const lodge of lodges) {
    const items = byLodgeId.get(lodge.id);
    if (items && items.length > 0) {
      groups.push({ lodgeId: lodge.id, lodgeName: lodge.name, items });
    }
  }
  if (unmatched.length > 0) {
    groups.push({ lodgeId: null, lodgeName: null, items: unmatched });
  }
  return groups;
}

export default function LodgeSearchResults({ results, nights, checkIn, checkOut, guests, scopedToLodge = false }: LodgeSearchResultsProps) {
  const t = useTranslations('bookingLodges');
  const { isSoldOut, loaded: soldOutLoaded } = useSoldOut();
  const { convertCLPtoUSD, loading: rateLoading } = useExchangeRate();

  // Un resultado se oculta si la unidad misma está agotada, o si pertenece a
  // un lodge agotado (settings/soldOut es la fuente dinámica; el campo
  // estático lodge.soldOut es el respaldo). Mismo criterio que
  // UnitBookingClientPage.tsx usa para bloquear el acceso directo.
  const visibleResults = useMemo(() => {
    return results.filter((result) => {
      if (isSoldOut(result.unitId, false)) return false;
      const lodge = lodges.find((l) => l.id === result.locationId);
      if (lodge && isSoldOut(lodge.id, !!lodge.soldOut)) return false;
      return true;
    });
  }, [results, isSoldOut]);

  const groups = useMemo(() => groupResultsByLodge(visibleResults), [visibleResults]);

  // Espera a que cargue el mapa de agotados antes de decidir qué mostrar:
  // evita renderizar una lista que un instante después se achique (mismo
  // patrón que soldOutLoaded en UnitBookingClientPage.tsx / visibilityLoaded
  // en LodgeUnitsClient.tsx).
  if (!soldOutLoaded) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
      </div>
    );
  }

  // El filtro dejó la lista vacía (todo lo encontrado estaba agotado):
  // mismo mensaje de "sin resultados" que ya usan page.tsx / LodgeUnitsClient.tsx.
  if (visibleResults.length === 0) {
    return (
      <div className={styles.noUnits}>
        <p>{t('search.noResults')}</p>
        <p>{t('search.noResultsHint')}</p>
      </div>
    );
  }

  return (
    <div className={styles.selectedView}>
      {groups.map((group, groupIndex) => (
        <div key={group.lodgeId ?? '__unmatched'}>
          {group.lodgeName && !scopedToLodge && (
            <h2
              className={styles.locationName}
              style={{ marginTop: groupIndex > 0 ? '2rem' : 0, marginBottom: '1rem' }}
            >
              {group.lodgeName}
            </h2>
          )}
          <div className={styles.unitsList}>
            {group.items.map((item) => {
              const unitType = item.unitType || 'room';
              const isPremium = unitType === 'premium';
              const isFullLodge = unitType === 'full_lodge';
              const cardClass = isPremium
                ? styles.unitCardPremium
                : isFullLodge
                  ? styles.unitCardLodge
                  : styles.unitCardRoom;
              const badgeClass = isPremium
                ? styles.typeBadgePremium
                : isFullLodge
                  ? styles.typeBadgeLodge
                  : styles.typeBadgeRoom;
              const badgeLabel = isPremium
                ? t('badge.premium')
                : isFullLodge
                  ? t('badge.fullLodge')
                  : t('badge.room');
              const ctaClass = isPremium
                ? styles.unitCtaPremium
                : isFullLodge
                  ? styles.unitCtaLodge
                  : styles.unitCtaRoom;

              const rawCurrency = (item.pricing.currency || '').toUpperCase();
              const isCLP = rawCurrency === 'CLP' || rawCurrency === 'PESO' || rawCurrency === 'PESOS';
              const totalUsd = isCLP ? convertCLPtoUSD(item.pricing.total) : item.pricing.total;
              const perNightUsd = isCLP ? convertCLPtoUSD(item.pricing.pricePerNight) : item.pricing.pricePerNight;
              // While the real exchange rate hasn't loaded yet, convertCLPtoUSD
              // uses a fallback rate — showing that number would flash a
              // different price the instant the real rate arrives. Skeleton
              // instead of a figure that's about to change.
              const priceReady = !isCLP || !rateLoading;

              const href = `/booking/lodges/${item.unitId}?checkIn=${checkIn}&checkOut=${checkOut}&guests=${guests}`;

              const cardContent = (
                <>
                  <div className={styles.unitImageWrap}>
                    <Image
                      src={item.image || FALLBACK_IMAGE}
                      alt={item.name}
                      fill
                      sizes="(max-width: 768px) 100px, 300px"
                      className={styles.unitImage}
                    />
                  </div>
                  <div className={styles.unitContent}>
                    <div className={styles.unitNameRow}>
                      <h3 className={styles.unitName}>{item.name}</h3>
                      <span className={`${styles.typeBadge} ${badgeClass}`}>
                        {isPremium && (
                          <svg className={styles.crownIcon} width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" />
                            <path d="M5 19h14v2H5z" />
                          </svg>
                        )}
                        {badgeLabel}
                      </span>
                    </div>
                    <p className={styles.unitMeta}>{t('search.upTo', { count: item.maxOccupancy })}</p>
                    <div className={styles.unitPricing}>
                      {priceReady ? (
                        <span className={styles.unitPrice}>{formatUSD(totalUsd)}</span>
                      ) : (
                        <span className={`${styles.unitPrice} ${skeletonStyles.skeletonPulseWide}`} aria-hidden="true" />
                      )}
                    </div>
                    <p className={styles.unitMeta}>
                      {priceReady ? (
                        formatUSD(perNightUsd)
                      ) : (
                        <span className={skeletonStyles.skeletonPulse} aria-hidden="true" />
                      )}{' '}
                      {t('search.perNight')} &middot; {t('search.nightsTotal', { count: nights })}
                    </p>
                    <span className={`${styles.unitCta} ${ctaClass}`}>
                      {t('viewDetailsBook')}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </>
              );

              return (
                <Link key={item.unitId} href={href} className={`${styles.unitCard} ${cardClass}`}>
                  {cardContent}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
